import { useState, useCallback } from 'react';

interface CopyButtonProps {
  text: string;
  label?: string;
  copiedLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function CopyButton({
  text,
  label = 'Copy',
  copiedLabel = 'Copied!',
  className,
  style,
  children,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback silently
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={className}
      style={style}
      title={copied ? copiedLabel : label}
    >
      {children || (copied ? copiedLabel : label)}
    </button>
  );
}

/**
 * Hook for inline copy-to-clipboard with feedback state.
 * Use when you need copy behavior without a dedicated button.
 */
export function useCopyToClipboard(timeout = 2000) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copy = useCallback(async (text: string, id?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id || 'default');
      setTimeout(() => setCopiedId(null), timeout);
      return true;
    } catch {
      return false;
    }
  }, [timeout]);

  return { copy, copiedId, isCopied: (id?: string) => copiedId === (id || 'default') };
}
